/**
 * Modern Authentication Page for THPT App
 * Supports login, registration with smooth transitions
 * Based on student app concept but with THPT branding
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl } from '../utils/siteConfig';
import { Tabs, TabsList, TabsTrigger } from '@vsat/shared/components';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { thptTheme } from '../theme/thpt-theme';
import { Logo } from '../components/Logo';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { getPostAuthRedirect } from '../utils/authRedirect';

type AuthMode = 'login' | 'register';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Determine initial mode from pathname
  const initialMode = location.pathname === '/register' ? 'register' : 'login';
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const { isAuthenticated } = useAuthStore();

  // Update mode when route changes
  useEffect(() => {
    if (location.pathname === '/register') {
      setMode('register');
    } else if (location.pathname === '/login') {
      setMode('login');
    }
  }, [location.pathname]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const destination = getPostAuthRedirect(location.search);
      navigate(destination, { replace: true });
      return;
    }
  }, [isAuthenticated, navigate, location.search]);

  const handleLoginSuccess = () => {
    console.log('Login successful, redirecting...');
    const destination = getPostAuthRedirect(location.search);
    navigate(destination, { replace: true });
  };

  const handleRegisterSuccess = () => {
    console.log('Registration successful');
    setMode('login');
  };

  const handleNeedsVerification = (email: string) => {
    // For now, just show success message
    // OTP verification can be added later if needed
    console.log('Verification needed for:', email);
  };

  // Don't render if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const isLogin = mode === 'login';
  const seoTitle = isLogin
    ? 'Đăng nhập | Hướng nghiệp CAT'
    : 'Đăng ký tài khoản | Hướng nghiệp CAT';
  const seoDesc = isLogin
    ? 'Đăng nhập tài khoản Hướng nghiệp CAT - Thi thử TN THPT và trắc nghiệm hướng nghiệp.'
    : 'Đăng ký tài khoản Hướng nghiệp CAT - Thi thử TN THPT miễn phí và trắc nghiệm hướng nghiệp.';

  return (
    <div className='min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        canonicalUrl={getCanonicalUrl(isLogin ? '/login' : '/register')}
      />
      <UnifiedHeader />
      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className='container relative flex items-center justify-center px-4 py-8'
        >
          {/* Background Pattern */}
          <div className='absolute inset-0 bg-grid-slate-200/50 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] pointer-events-none' />

          {/* Auth Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className='relative w-full max-w-6xl'
          >
            <div className='grid lg:grid-cols-2 gap-8 lg:gap-12 items-center'>
              {/* Left Side - THPT Branding */}
              <div className='hidden lg:block space-y-6'>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className='inline-flex items-center gap-3 mb-6'>
                    <div className='w-12 h-12 bg-primary rounded-xl flex items-center justify-center'>
                      <GraduationCap className='w-7 h-7 text-white' />
                    </div>
                    <div>
                      <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                        {thptTheme.name}
                      </h2>
                      <p className='text-sm text-gray-600 dark:text-gray-300'>
                        {thptTheme.tagline}
                      </p>
                    </div>
                  </div>

                  <h1 className='text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight'>
                    {mode === 'login' && 'Chào mừng trở lại'}
                    {mode === 'register' && 'Bắt đầu hành trình của bạn'}
                  </h1>

                  <p className='text-lg text-gray-600 dark:text-gray-300 max-w-md'>
                    {mode === 'login' &&
                      'Đăng nhập để tiếp tục luyện thi THPT và khám phá định hướng nghề nghiệp'}
                    {mode === 'register' &&
                      'Tạo tài khoản để truy cập ngân hàng đề thi THPT và công cụ định hướng nghề nghiệp'}
                  </p>

                  {/* Features List */}
                  <div className='space-y-3 pt-8'>
                    {thptTheme.features.slice(0, 4).map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className='flex items-center gap-3'
                      >
                        <div className='w-5 h-5 bg-primary-light dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0'>
                          <svg
                            className='w-3 h-3 text-primary dark:text-yellow-400'
                            fill='none'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path d='M5 13l4 4L19 7' />
                          </svg>
                        </div>
                        <span className='text-sm text-gray-700 dark:text-gray-300'>
                          {feature.title}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Right Side - Auth Forms */}
              <div className='relative'>
                <div className='bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-gray-700/50 p-8 lg:p-10'>
                  {/* Tab Switcher */}
                  <Tabs
                    value={mode}
                    onValueChange={value => {
                      setMode(value as AuthMode);
                      // Update URL when switching tabs
                      navigate(value === 'register' ? '/register' : '/login', { replace: true });
                    }}
                    className='mb-8'
                  >
                    <TabsList className='grid w-full grid-cols-2'>
                      <TabsTrigger value='login'>Đăng nhập</TabsTrigger>
                      <TabsTrigger value='register'>Đăng ký</TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {/* Forms with Animation */}
                  <AnimatePresence mode='wait'>
                    {mode === 'login' && (
                      <motion.div
                        key='login'
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <LoginForm
                          onSwitchToRegister={() => setMode('register')}
                          onForgotPassword={() => {
                            toast.info('Chức năng đang phát triển');
                          }}
                          onSuccess={handleLoginSuccess}
                          onNeedsVerification={handleNeedsVerification}
                        />
                      </motion.div>
                    )}

                    {mode === 'register' && (
                      <motion.div
                        key='register'
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <RegisterForm
                          onSwitchToLogin={() => setMode('login')}
                          onSuccess={handleRegisterSuccess}
                          onNeedsVerification={handleNeedsVerification}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Branding */}
                <div className='lg:hidden mt-8 text-center'>
                  <div className='inline-flex items-center gap-2 text-sm text-gray-600'>
                    <GraduationCap className='w-4 h-4' />
                    <span>{thptTheme.fullName}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
